import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GeneralIcon } from "./flowIcons";
import { ROUTE_DETAIL_TABS } from "./routeSubpages";

// Floating vertical navigation for the Route details page. The Configurations
// tab can get long enough that the tab strip scrolls far out of view, and with
// it any hint that Sources and Logs exist — this rail appears once the header
// has left the viewport so switching tabs never requires scrolling back up.
// RouteSectionRail is presentational; FloatingRouteSectionRail below owns the
// visibility question.


// A tracker device: a small unit with an antenna and a signal wave — the thing a
// Source actually is out in the field.
const DeviceIcon = ({ className = "" }: { className?: string }) => (
    <svg className={`inline-block flex-none ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="7" width="8" height="6.5" rx="1.5" />
        <line x1="6.5" y1="7" x2="6.5" y2="3.5" />
        <path d="M9.5 2a4.5 4.5 0 0 1 3.5 2" />
        <circle cx="8" cy="10.25" r="1" />
    </svg>
);

const LogsIcon = ({ className = "" }: { className?: string }) => (
    <svg className={`inline-block flex-none ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
        <line x1="3" y1="4" x2="13" y2="4" /><line x1="3" y1="8" x2="13" y2="8" /><line x1="3" y1="12" x2="9.5" y2="12" />
    </svg>
);

const RouteSectionRail = ({ activeSubpage, onSelectSubPage }: {
    activeSubpage?: string;
    onSelectSubPage: (slug: string) => void;
}) => {
    const { t } = useTranslation();
    const icons: Record<string, React.ReactNode> = {
        configurations: <GeneralIcon />,
        sources: <DeviceIcon />,
        logs: <LogsIcon />,
    };
    const tabs = ROUTE_DETAIL_TABS.map(({ slug, labelKey }) => ({
        slug, label: t(labelKey), icon: icons[slug] ?? <GeneralIcon />,
    }));
    return (
        /* The page content is a centered 1080px column (max-w-list-table), so a
           fixed rail only has a real gutter to sit in on wide viewports:
           icons (~60px with the edge offset) fit from ~1200px, labels (~170px)
           from ~1440px. Below that the rail would cover the content it links
           to, so it steps down to icons and then hides. */
        <nav aria-label={t("Route sections")}
            data-testid="RouteSectionRail-nav"
            className="hidden desktop:flex fixed left-4 top-1/2 -translate-y-1/2 z-30 flex-col gap-1 p-1.5 bg-white border border-gray-lines rounded-lg shadow-md">
            {tabs.map((tab) => (
                <button key={tab.slug} type="button"
                    title={tab.label}
                    aria-current={activeSubpage === tab.slug ? "page" : undefined}
                    onClick={() => onSelectSubPage(tab.slug)}
                    className={`flex items-center gap-2 rounded px-2.5 py-2 text-sm text-left transition-colors ${
                        activeSubpage === tab.slug
                            ? "bg-light-gray-button text-route-green font-semibold"
                            : "text-secondary-text hover:text-route-green hover:bg-light-gray"}`}>
                    {tab.icon}
                    {/* Icon-only until the gutter is wide enough for labels —
                        the labels live in the tooltips meanwhile. */}
                    <span className="hidden min-[1440px]:inline">{tab.label}</span>
                </button>
            ))}
        </nav>
    );
};

// Owns the "has the header scrolled away?" question, so the page component
// doesn't re-render its whole tree (flow map included) every time the header
// crosses the viewport edge — only this ~60-line subtree toggles.
export const FloatingRouteSectionRail = ({ headerRef, activeSubpage, onSelectSubPage }: {
    headerRef: React.RefObject<HTMLDivElement>;
    activeSubpage?: string;
    onSelectSubPage: (slug: string) => void;
}) => {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = headerRef.current;
        if (!el || typeof IntersectionObserver === "undefined") return;
        const observer = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting));
        observer.observe(el);
        return () => observer.disconnect();
    }, [headerRef]);
    if (!visible) return null;
    return <RouteSectionRail activeSubpage={activeSubpage} onSelectSubPage={onSelectSubPage} />;
};

export default RouteSectionRail;
